import { useCallback, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";

import { getLocaleText } from "../actions/datagrid";
import {
  contextMenu,
  defaultColDef,
  sideBar,
  statusBar,
  gridSettings,
  saveViewState,
  loadViewState,
  restoreViewState,
} from "../common/grid";
import { restoreIconString } from "../common/icons";
import { eSalaryGet, procountorWorkingHourReductionQuery } from "../api";
import { useDispatch, useSelector } from "react-redux";
import { buildAlertWithLocalizedMessage } from "../actions";
import { PdfReport } from "../common/pdfreport";
import { activeIntegrationsSelector, authSelector } from "../reducers";
import { dateFormatter, eSalaryDateGetter, jsError, numberFormatterFraction } from "../common/utils";

import { queryClient } from "../main";
import { Button } from "../common/Button.tsx";

const viewStateKey = "pekkasetViewState";

export function PekkasetView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef();

  const { companyName } = useSelector(authSelector);
  const activeIntegrations = useSelector(activeIntegrationsSelector);
  const isProcountorActive = !!activeIntegrations.tokenProcountor;

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  const { data, isLoading } = useQuery(
    isProcountorActive
      ? procountorWorkingHourReductionQuery
      : {
        queryKey: ["pekkaset"],
        queryFn: async () => {
          try {
            const res = await eSalaryGet("pekkaset");
            const json = res.data.pekkaset_response["pekkascalc"] ?? [];
            json.forEach((item) => delete item.id);
            return json;
          } catch (err) {
            jsError(err);
            dispatch(buildAlertWithLocalizedMessage("ERROR", "StatisticsFetchError", "Virhe tilaston haussa"));
            return [];
          }
        },
      },
  );

  const procountorColumnDefs = [
    {
      headerName: t("Number", "Numero"),
      field: "employee.id",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 80,
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
      valueGetter: (params) =>
        params.data && params.data.employee ? `${params.data.employee.lastName} ${params.data.employee.firstName}` : "",
      sortable: true,
      filter: "agMultiColumnFilter",
    },
    {
      headerName: t("ReductionStartingDate", "Pekkaslaskennan aloituspäivämäärä"),
      field: "reductionstart",
      valueGetter: (params) => params.data?.workingHourReduction?.startDate ?? "",
      sortable: true,
      filter: "agDateColumnFilter",
      hide: true,
    },
    {
      headerName: t("RemainingReductionHours", "Pekkastunteja jäljellä"),
      field: "reductionhours",
      valueGetter: (params) => params.data?.workingHourReduction?.totalRemainingWorkingHourReductionHours ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("RemainingReductionDays", "Pekkaspäiviä jäljellä"),
      field: "reductiondays",
      valueGetter: (params) => params.data?.workingHourReduction?.totalRemainingWorkingHourReductionDays ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
  ];

  const eSalaryColumnDefs = [
    {
      headerName: t("PersonNumber", "Nro"),
      field: "number",
    },
    {
      headerName: t("Person", "Nimi"),
      field: "name",
      filter: "agMultiColumnFilter",
    },
    {
      headerName: t("Year", "Vuosi"),
      field: "year",
    },
    {
      headerName: t("Month", "Kuukausi"),
      field: "month",
    },
    {
      headerName: t("Date", "Päivämäärä"),
      field: "date",
      cellClass: "dateType",
      filter: "agDateColumnFilter",
      valueGetter: eSalaryDateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("PaymentPeriod", "Maksukausi"),
      field: "period",
      filter: "agMultiColumnFilter",
    },
    {
      headerName: t("Earned", "Ansaitut"),
      field: "earned",
      valueFormatter: numberFormatterFraction,
    },
    {
      headerName: t("Used", "Käytetyt"),
      field: "used",
    },
    {
      headerName: t("Remaining", "Jäljellä"),
      field: "remaining",
      valueFormatter: numberFormatterFraction,
    },
    {
      headerName: t("SaldoReset", "Saldonollaus"),
      field: "saldoreset",
    },
    {
      headerName: t("Saldo", "Saldo"),
      field: "saldo",
      valueFormatter: numberFormatterFraction,
    },
  ];

  const columnDefs = isProcountorActive ? procountorColumnDefs : eSalaryColumnDefs;

  const initialState = {
    columnVisibility: {
      hiddenColIds: [],
    },
  };

  const getContextMenuItems = useCallback(
    (params) => {
      let items = [];

      items.push({
        name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
        action: function () {
          restoreViewState(viewStateKey, initialState);
        },
        icon: restoreIconString,
      });

      items.push("separator");

      items = items.concat(
        contextMenu(t, params, setReport, {
          title: t("PekkasStatistics", "Pekkastilastot"),
          companyname: companyName,
        }),
      );
      return items;
    },
    [companyName, t],
  );

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
      <Row className="my-1">
        <Col sm="4">
          <h2 className="viewheading">{t("PekkasStatistics", "Pekkastilastot")}</h2>
        </Col>
        <Col sm="8" align="right">
          {!isProcountorActive && (
            <Button
              variant="primary"
              disabled={isLoading}
              onClick={() =>
                queryClient.invalidateQueries({
                  queryKey: [isProcountorActive ? "procountorWorkingHourReduction" : "pekkaset"],
                })
              }
            >
              {t("Update", "Päivitä")}
            </Button>
          )}
        </Col>
      </Row>
      <Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
        <AgGridReact
          {...gridSettings}
          loading={isLoading}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={{ ...defaultColDef, filter: "agNumberColumnFilter" }}
          sideBar={sideBar}
          rowData={
            isProcountorActive ? (Array.isArray(data) ? data.filter((row) => !!row.workingHourReduction) : []) : data
          }
          getContextMenuItems={getContextMenuItems}
          statusBar={statusBar}
          getLocaleText={(params) => getLocaleText(t, params)}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          initialState={loadViewState(viewStateKey) || initialState}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
